import { useMemo } from 'react';
import { SelectInput, SelectInputProps, useGetList } from 'react-admin';

import { B2bPlanInstrumentsCashSources } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';

type InstrumentSelectInputProps = Omit<SelectInputProps, 'choices'> & {
  instrumentIds: number[];
  isCashSourcesPerInstrument: boolean;
  instrumentsCashSourcesLibre: B2bPlanInstrumentsCashSources;
};

export const InstrumentSelectInput = ({
  instrumentIds,
  isCashSourcesPerInstrument,
  instrumentsCashSourcesLibre,
  ...props
}: InstrumentSelectInputProps) => {
  // When cash sources are configured per instrument, only keep instruments that
  // accept voluntary deposits
  const eligibleInstrumentIds = useMemo(
    () =>
      isCashSourcesPerInstrument
        ? instrumentIds.filter((instrumentId) =>
            instrumentsCashSourcesLibre
              .find((entry) => entry.instrument_id === instrumentId)
              ?.cash_sources?.includes('voluntary_deposit'),
          )
        : instrumentIds,
    [isCashSourcesPerInstrument, instrumentIds, instrumentsCashSourcesLibre],
  );

  const { data = [], isLoading } = useGetList<GetInstrumentsResponseItem>(
    'instruments',
    {
      filter: { id: eligibleInstrumentIds },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: eligibleInstrumentIds.length > 0 },
  );

  const choices = useMemo(
    () =>
      data.map((instrument) => {
        const displayName = instrument.name || instrument.symbol;
        const fullName = instrument.isin ? `${displayName} - ${instrument.isin}` : displayName;
        return {
          id: instrument.id,
          name: fullName,
        };
      }),
    [data],
  );

  return (
    <SelectInput
      data-testid="BoExternalB2bVoluntaryPaymentInstrumentSelectInput"
      {...props}
      choices={choices}
      optionText="name"
      optionValue="id"
      isLoading={isLoading}
    />
  );
};
